// @flow

import * as urls from '../urls';
import * as request from 'app/utils/request';
import {gt} from 'app/utils/i18n';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';

import type {NotificationsFetchParamsType, NotificationType} from './types';

import type {ResponseType} from 'app/utils/redux_v2';
import type {ShareableContentType} from 'app/user/types';
import {limitRequests} from 'app/utils/redux';

import {type NotificationModelChangeMessageType} from 'app/subscriptions/types';
import {showNotification} from 'app/toasts/slice';
import {getContentType} from 'app/utils/content-type';

import {
  subscribeFetcher,
  subscriptionStatusFetcher,
  unsubscribeFetcher,
} from 'app/notifly/fetchers';

export type NotificationsAction =
  | ReceiveNotificationsStatsActionType
  | RequestNotificationsActionType
  | HideBannerActionType
  | ReceiveNotificationsListActionType;

/* receive notifications stats */
export const receiveNotificationsStats = (
  data: NotificationModelChangeMessageType
): ReceiveNotificationsStatsActionType => ({
  type: RECEIVE_NOTIFICATIONS_STATS,
  data,
});
export const RECEIVE_NOTIFICATIONS_STATS = 'RECEIVE_NOTIFICATIONS_STATS';

type ReceiveNotificationsStatsActionType = {
  +type: 'RECEIVE_NOTIFICATIONS_STATS',
  data: NotificationModelChangeMessageType,
};

export const REQUEST_NOTIFICATIONS = 'REQUEST_NOTIFICATIONS';

type RequestNotificationsActionType = {
  +type: 'REQUEST_NOTIFICATIONS',
  currentFilters: NotificationsFetchParamsType,
};

export const HIDE_BANNER = 'HIDE_BANNER';

type HideBannerActionType = {
  +type: 'HIDE_BANNER',
  show: boolean,
};

export const hideBanner = (show: boolean): HideBannerActionType => ({
  type: HIDE_BANNER,
  show,
});

export const setHideBanner = (show: boolean) => (dispatch: SimpleDispatch) =>
  dispatch(hideBanner(show));
export const RECEIVE_NOTIFICATIONS_LIST = 'RECEIVE_NOTIFICATIONS_LIST';

type ReceiveNotificationsListActionType = {
  +type: 'RECEIVE_NOTIFICATIONS_LIST',
  query: NotificationsFetchParamsType,
  response: ResponseType<NotificationType>,
};

export const receiveNotifications = (
  query: NotificationsFetchParamsType,
  response: ResponseType<NotificationType>
): ReceiveNotificationsListActionType => ({
  type: RECEIVE_NOTIFICATIONS_LIST,
  query,
  response,
});

export const convertOptionsToFetchParams = (
  opts: $Shape<NotificationsFetchParamsType>
): NotificationsFetchParamsType => ({
  page: opts.page || '1',
  page_size: opts.page_size || DEFAULT_PAGE_SIZE,
  target_content_type: opts.target_content_type,
  target_object_id: opts.target_object_id,
  web_visible: opts.web_visible != null ? opts.web_visible : true,
  is_all: opts.is_all,
});

export const fetchNotificationsList = limitRequests(
  (opts?: $Shape<NotificationsFetchParamsType> = {}) =>
    (dispatch: SimpleDispatch) => {
      const query = convertOptionsToFetchParams(opts);

      return request
        .get(urls.NOTIFICATIONS_API, query)
        .then(
          (response) => dispatch(receiveNotifications(query, response)),
          request.createErrorHandler(
            gt('Could not fetch notifications.'),
            dispatch
          )
        );
    }
);

export const markAllAsRead = () => (dispatch: FuncDispatch) => {
  return request
    .postJson(urls.NOTIFICATIONS_MARK_READ)
    .then(() => dispatch(fetchNotificationsList()))
    .catch(
      request.createErrorHandler(
        gt('Could not mark notifications as read.'),
        dispatch
      )
    );
};

export const subscribe =
  (object_id: number, content_type: ShareableContentType) =>
  (dispatch: FuncDispatch) =>
    subscribeFetcher(object_id, content_type).then((data) => {
      const ctName = getContentType(content_type).name;
      dispatch(
        showNotification(
          gt(`You are now subscribed to notifications for this ${ctName}.`)
        )
      );
      return data;
    }, request.createErrorHandler(gt('Could subscribe to document'), dispatch));

export const unsubscribe =
  (url: string, content_type: ShareableContentType) =>
  (dispatch: FuncDispatch) =>
    unsubscribeFetcher(url).then((data) => {
      const ctName = getContentType(content_type).name;
      dispatch(
        showNotification(
          gt(`You have unsubscribed from notifications for this ${ctName}.`)
        )
      );
      return data;
    }, request.createErrorHandler(gt('Could not unsubscribe from document'), dispatch));

export const fetchSubscriptionStatus = (
  object_id: number,
  content_type: 'procedure' | 'proceduregroup' | 'policy' | 'tasktemplate'
) => subscriptionStatusFetcher(object_id, content_type);
